/**
 * Mise en forme des valeurs numériques d’une cellule .xlsx selon son format de nombre,
 * pour obtenir le texte affiché (milliers « , », décimale « . », mois en anglais), comme le
 * faisait la lecture précédente. Sous-ensemble volontaire : General, 0, 0.00, #,##0, %, E+,
 * littéraux, sections positif;négatif;zéro, dates et heures usuelles. Tout format non géré
 * (fractions, conditions, durées [h]) retombe sur General.
 *
 * Écart assumé : les formats de date « courte système » (identifiants intégrés 14 et 22, que
 * l’ancienne lecture rendait en « m/d/yy », ambigus et à année sur 2 chiffres) sont rendus en
 * ISO « aaaa-mm-jj » / « aaaa-mm-jj hh:mm ».
 */

/** Formats intégrés (numFmtId → code). */
export const FORMATS_INTEGRES: Readonly<Record<number, string>> = {
  0: 'General',
  1: '0',
  2: '0.00',
  3: '#,##0',
  4: '#,##0.00',
  9: '0%',
  10: '0.00%',
  11: '0.00E+00',
  14: 'yyyy-mm-dd',
  15: 'd-mmm-yy',
  16: 'd-mmm',
  17: 'mmm-yy',
  18: 'h:mm AM/PM',
  19: 'h:mm:ss AM/PM',
  20: 'h:mm',
  21: 'h:mm:ss',
  22: 'yyyy-mm-dd hh:mm',
  37: '#,##0 ;(#,##0)',
  38: '#,##0 ;[Red](#,##0)',
  39: '#,##0.00;(#,##0.00)',
  40: '#,##0.00;[Red](#,##0.00)',
  45: 'mm:ss',
  46: '[h]:mm:ss',
  47: 'mmss.0',
  48: '##0.0E+0',
  49: '@',
};

const MOIS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const JOURS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function retirerZerosDecimaux(o: string): string {
  return o.indexOf('.') === -1 ? o : o.replace(/(?:\.0*|(\.\d*[1-9])0+)$/, '$1');
}

function normaliserExposant(o: string): string {
  if (o.indexOf('E') === -1) return o;
  return o.replace(/(?:\.0*|(\.\d*[1-9])0+)[Ee]/, '$1E').replace(/(E[+-])(\d)$/, '$10$2');
}

/** Format « Standard » d’Excel : 11 caractères au plus pour un nombre. */
export function formatGeneral(v: number): string {
  if (!Number.isFinite(v)) return '';
  if ((v | 0) === v) return v.toString(10);
  const e = Math.floor(Math.log(Math.abs(v)) * Math.LOG10E);
  let o: string;
  if (e >= -4 && e <= -1) {
    o = v.toPrecision(10 + e);
  } else if (Math.abs(e) <= 9) {
    const w = v < 0 ? 12 : 11;
    o = retirerZerosDecimaux(v.toFixed(12));
    if (o.length > w) {
      o = v.toPrecision(10);
      if (o.length > w) o = v.toExponential(5);
    }
  } else if (e === 10) {
    o = v.toFixed(10).slice(0, 12);
  } else {
    o = retirerZerosDecimaux(v.toFixed(11));
    if (o.length > (v < 0 ? 12 : 11) || o === '0' || o === '-0') o = v.toPrecision(6);
  }
  return retirerZerosDecimaux(normaliserExposant(o.toUpperCase()));
}

type Jeton =
  | { t: 'lit'; s: string }
  | { t: 'dig'; c: '0' | '#' | '?' }
  | { t: 'dec' }
  | { t: 'sep' }
  | { t: 'pct' }
  | { t: 'exp'; signe: '+' | '-' }
  | { t: 'date'; c: 'y' | 'm' | 'd' | 'h' | 's'; n: number }
  | { t: 'ampm'; court: boolean }
  | { t: 'texte' }
  | { t: 'general' };

interface Section {
  jetons: Jeton[];
  nonGere: boolean;
}

const COULEURS = /^(black|blue|cyan|green|magenta|red|white|yellow|color\s*\d+)$/i;

function analyserSection(code: string): Section {
  const jetons: Jeton[] = [];
  let nonGere = false;
  let i = 0;
  while (i < code.length) {
    const c = code[i]!;
    if (c === '"') {
      const fin = code.indexOf('"', i + 1);
      const f = fin === -1 ? code.length : fin;
      jetons.push({ t: 'lit', s: code.slice(i + 1, f) });
      i = f + 1;
    } else if (c === '\\') {
      if (i + 1 < code.length) jetons.push({ t: 'lit', s: code[i + 1]! });
      i += 2;
    } else if (c === '_') {
      jetons.push({ t: 'lit', s: ' ' });
      i += 2;
    } else if (c === '*') {
      i += 2;
    } else if (c === '[') {
      const fin = code.indexOf(']', i);
      const f = fin === -1 ? code.length : fin;
      const interieur = code.slice(i + 1, f);
      if (interieur.startsWith('$')) {
        const symbole = interieur.slice(1).split('-')[0] ?? '';
        if (symbole) jetons.push({ t: 'lit', s: symbole });
      } else if (!COULEURS.test(interieur)) {
        nonGere = true;
      }
      i = f + 1;
    } else if (/[yYdD]/.test(c)) {
      let n = 1;
      while (code[i + n]?.toLowerCase() === c.toLowerCase()) n++;
      jetons.push({ t: 'date', c: c.toLowerCase() as 'y' | 'd', n });
      i += n;
    } else if (/[mM]/.test(c)) {
      let n = 1;
      while (code[i + n]?.toLowerCase() === 'm') n++;
      jetons.push({ t: 'date', c: 'm', n });
      i += n;
    } else if (/[hH]/.test(c)) {
      let n = 1;
      while (code[i + n]?.toLowerCase() === 'h') n++;
      jetons.push({ t: 'date', c: 'h', n });
      i += n;
    } else if (/[sS]/.test(c)) {
      let n = 1;
      while (code[i + n]?.toLowerCase() === 's') n++;
      jetons.push({ t: 'date', c: 's', n });
      i += n;
    } else if (/^AM\/PM/i.test(code.slice(i, i + 5))) {
      jetons.push({ t: 'ampm', court: false });
      i += 5;
    } else if (/^A\/P/i.test(code.slice(i, i + 3))) {
      jetons.push({ t: 'ampm', court: true });
      i += 3;
    } else if (/^General/i.test(code.slice(i, i + 7))) {
      jetons.push({ t: 'general' });
      i += 7;
    } else if ((c === 'E' || c === 'e') && (code[i + 1] === '+' || code[i + 1] === '-')) {
      jetons.push({ t: 'exp', signe: code[i + 1] as '+' | '-' });
      i += 2;
    } else if (c === '0' || c === '#' || c === '?') {
      jetons.push({ t: 'dig', c });
      i++;
    } else if (c === '.') {
      jetons.push({ t: 'dec' });
      i++;
    } else if (c === ',') {
      jetons.push({ t: 'sep' });
      i++;
    } else if (c === '%') {
      jetons.push({ t: 'pct' });
      i++;
    } else if (c === '@') {
      jetons.push({ t: 'texte' });
      i++;
    } else if (/[A-Za-z]/.test(c)) {
      nonGere = true;
      i++;
    } else {
      jetons.push({ t: 'lit', s: c });
      i++;
    }
  }
  return { jetons, nonGere };
}

/** Découpe un code en sections sur les « ; » hors guillemets, échappements et crochets. */
function decouper(code: string): string[] {
  const sections: string[] = [];
  let cur = '';
  let guillemets = false;
  let crochets = false;
  for (let i = 0; i < code.length; i++) {
    const c = code[i]!;
    if (guillemets) {
      cur += c;
      if (c === '"') guillemets = false;
    } else if (c === '"') {
      guillemets = true;
      cur += c;
    } else if (c === '\\' || c === '_' || c === '*') {
      cur += c + (code[i + 1] ?? '');
      i++;
    } else if (c === '[') {
      crochets = true;
      cur += c;
    } else if (c === ']') {
      crochets = false;
      cur += c;
    } else if (c === ';' && !crochets) {
      sections.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  sections.push(cur);
  return sections;
}

const cache = new Map<string, Section[]>();

function sectionsDe(code: string): Section[] {
  let s = cache.get(code);
  if (!s) {
    s = decouper(code).map(analyserSection);
    if (cache.size > 500) cache.clear();
    cache.set(code, s);
  }
  return s;
}

function estDate(s: Section): boolean {
  return s.jetons.some((j) => j.t === 'date' || j.t === 'ampm');
}

/** Arrondi à `decimales` décimales (demi vers l’infini sur la valeur absolue). */
function arrondir(v: number, decimales: number): number {
  if (v >= 1e15) return v;
  const k = 10 ** decimales;
  return Math.round(v * k) / k;
}

function grouper(entier: string): string {
  return entier.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function rendreNombre(valeur: number, s: Section, moins: boolean): string | null {
  const signeMoins = moins;
  const jetons = s.jetons;
  const premier = jetons.findIndex((j) => j.t === 'dig' || j.t === 'dec');
  if (premier === -1) {
    // Aucun emplacement de chiffre : texte littéral seul.
    return (signeMoins ? '-' : '') + jetons.map((j) => (j.t === 'lit' ? j.s : '')).join('');
  }
  let dernier = premier;
  jetons.forEach((j, idx) => {
    if (j.t === 'dig' || j.t === 'dec' || j.t === 'exp') dernier = idx;
  });
  while (jetons[dernier + 1]?.t === 'sep') dernier++;
  const region = jetons.slice(premier, dernier + 1);
  let exposant: { signe: '+' | '-'; chiffres: number } | null = null;
  let mantisse = region;
  const iExp = region.findIndex((j) => j.t === 'exp');
  if (iExp !== -1) {
    mantisse = region.slice(0, iExp);
    const apres = region.slice(iExp + 1);
    if (apres.length === 0 || apres.some((j) => j.t !== 'dig')) return null;
    exposant = { signe: (region[iExp] as { signe: '+' | '-' }).signe, chiffres: apres.length };
  }
  for (const j of mantisse) {
    if (j.t !== 'dig' && j.t !== 'dec' && j.t !== 'sep') return null;
  }
  if (mantisse.filter((j) => j.t === 'dec').length > 1) return null;
  const iDec = mantisse.findIndex((j) => j.t === 'dec');
  const partieEntiere = iDec === -1 ? mantisse : mantisse.slice(0, iDec);
  const partieFrac = iDec === -1 ? [] : mantisse.slice(iDec + 1);
  if (partieFrac.some((j) => j.t === 'sep')) return null;
  const phEntier = partieEntiere.filter((j) => j.t === 'dig') as Array<{ t: 'dig'; c: string }>;
  const phFrac = partieFrac as Array<{ t: 'dig'; c: string }>;
  // Séparateurs : milliers (entre deux emplacements) ou échelle (après le dernier emplacement).
  let milliers = false;
  let echelle = 0;
  let vu = 0;
  for (const j of partieEntiere) {
    if (j.t === 'dig') vu++;
    else if (j.t === 'sep') {
      if (vu > 0 && vu < phEntier.length) milliers = true;
      else if (vu === phEntier.length && vu > 0) echelle++;
    }
  }
  const pct = jetons.filter((j) => j.t === 'pct').length;
  let v = Math.abs(valeur) * 100 ** pct;
  v /= 1000 ** echelle;
  const decimales = phFrac.length;
  const minEntier = phEntier.filter((j) => j.c === '0').length;
  let corps: string;
  if (exposant) {
    if (phEntier.length !== 1) return null;
    let e = v === 0 ? 0 : Math.floor(Math.log10(v));
    let m = v === 0 ? 0 : v / 10 ** e;
    m = arrondir(m, decimales);
    if (m >= 10) {
      m /= 10;
      e += 1;
    }
    const f = m.toFixed(decimales);
    const fracPart = f.includes('.') ? f.split('.')[1]! : '';
    let frac = fracPart;
    for (let k = phFrac.length - 1; k >= 0 && phFrac[k]!.c === '#' && frac.endsWith('0'); k--) {
      frac = frac.slice(0, -1);
    }
    const sgn = e < 0 ? '-' : exposant.signe === '+' ? '+' : '';
    corps = `${Math.trunc(m)}${frac ? '.' + frac : ''}E${sgn}${String(Math.abs(e)).padStart(exposant.chiffres, '0')}`;
  } else {
    const r = arrondir(v, decimales);
    if (r >= 1e21) return null;
    const txt = r.toFixed(decimales);
    let [ent, frac = ''] = txt.split('.') as [string, string?];
    ent = ent.replace(/^0+(?=\d)/, '');
    if (ent === '0' && minEntier === 0) ent = '';
    ent = ent.padStart(minEntier, '0');
    if (milliers && ent) ent = grouper(ent);
    for (let k = phFrac.length - 1; k >= 0 && phFrac[k]!.c !== '0' && frac.endsWith('0'); k--) {
      frac = frac.slice(0, -1);
    }
    corps = iDec === -1 ? ent : `${ent}.${frac}`;
  }
  let out = '';
  let corpsEcrit = false;
  jetons.forEach((j, idx) => {
    if (idx >= premier && idx <= dernier) {
      if (!corpsEcrit) {
        out += corps;
        corpsEcrit = true;
      }
      return;
    }
    if (j.t === 'lit') out += j.s;
    else if (j.t === 'pct') out += '%';
  });
  return (signeMoins ? '-' : '') + out;
}

function composantesDate(
  serie: number,
  date1904: boolean,
): { y: number; m: number; d: number; h: number; mi: number; s: number; dow: number } | null {
  if (!Number.isFinite(serie) || serie < 0 || serie > 2958465.99999) return null;
  let jours = Math.floor(serie);
  let secondes = Math.round((serie - jours) * 86400);
  if (secondes >= 86400) {
    jours += 1;
    secondes -= 86400;
  }
  let y: number;
  let m: number;
  let d: number;
  let dow: number;
  if (date1904) {
    const dt = new Date(Date.UTC(1904, 0, 1 + jours));
    y = dt.getUTCFullYear();
    m = dt.getUTCMonth() + 1;
    d = dt.getUTCDate();
    dow = dt.getUTCDay();
  } else if (jours === 60) {
    y = 1900;
    m = 2;
    d = 29;
    dow = 3;
  } else if (jours < 60) {
    y = 1900;
    m = jours < 32 ? 1 : 2;
    d = jours < 32 ? jours : jours - 31;
    dow = (jours + 6) % 7;
  } else {
    const dt = new Date(Date.UTC(1899, 11, 30 + jours));
    y = dt.getUTCFullYear();
    m = dt.getUTCMonth() + 1;
    d = dt.getUTCDate();
    dow = dt.getUTCDay();
  }
  return {
    y,
    m,
    d,
    h: Math.floor(secondes / 3600),
    mi: Math.floor((secondes % 3600) / 60),
    s: secondes % 60,
    dow,
  };
}

function rendreDate(serie: number, s: Section, date1904: boolean): string | null {
  const c = composantesDate(serie, date1904);
  if (!c) return null;
  const jetons = s.jetons;
  const ampm = jetons.some((j) => j.t === 'ampm');
  const dates = jetons
    .map((j, idx) => ({ j, idx }))
    .filter((x) => x.j.t === 'date' || x.j.t === 'ampm');
  let out = '';
  jetons.forEach((j, idx) => {
    switch (j.t) {
      case 'lit':
        out += j.s;
        return;
      case 'ampm':
        out += j.court ? (c.h < 12 ? 'A' : 'P') : c.h < 12 ? 'AM' : 'PM';
        return;
      case 'date': {
        const pos = dates.findIndex((x) => x.idx === idx);
        const avant = pos > 0 ? dates[pos - 1]!.j : null;
        const apres = pos < dates.length - 1 ? dates[pos + 1]!.j : null;
        switch (j.c) {
          case 'y':
            out += j.n <= 2 ? String(c.y % 100).padStart(2, '0') : String(c.y).padStart(4, '0');
            return;
          case 'd':
            out +=
              j.n === 1
                ? String(c.d)
                : j.n === 2
                  ? String(c.d).padStart(2, '0')
                  : j.n === 3
                    ? JOURS[c.dow]!.slice(0, 3)
                    : JOURS[c.dow]!;
            return;
          case 'h': {
            const h = ampm ? c.h % 12 || 12 : c.h;
            out += j.n === 1 ? String(h) : String(h).padStart(2, '0');
            return;
          }
          case 's':
            out += j.n === 1 ? String(c.s) : String(c.s).padStart(2, '0');
            return;
          case 'm': {
            const minutes =
              j.n <= 2 &&
              ((avant?.t === 'date' && avant.c === 'h') ||
                (apres?.t === 'date' && apres.c === 's'));
            if (minutes) {
              out += j.n === 1 ? String(c.mi) : String(c.mi).padStart(2, '0');
            } else if (j.n === 1) out += String(c.m);
            else if (j.n === 2) out += String(c.m).padStart(2, '0');
            else if (j.n === 3) out += MOIS[c.m - 1]!.slice(0, 3);
            else if (j.n === 4) out += MOIS[c.m - 1]!;
            else out += MOIS[c.m - 1]!.slice(0, 1);
            return;
          }
        }
        return;
      }
      default:
        return;
    }
  });
  return out;
}

/**
 * Texte affiché d’un nombre pour un code de format donné (undefined = Standard).
 * `date1904` : le classeur utilise l’époque 1904.
 */
export function formaterNombre(valeur: number, code: string | undefined, date1904 = false): string {
  if (!Number.isFinite(valeur)) return '';
  if (code === undefined || code === '' || /^general$/i.test(code)) return formatGeneral(valeur);
  const sections = sectionsDe(code);
  if (sections.length > 4 || sections.some((s) => s.nonGere)) return formatGeneral(valeur);
  let choisie: Section;
  let moins = false;
  if (sections.length === 1) {
    choisie = sections[0]!;
    moins = valeur < 0;
  } else if (valeur > 0) {
    choisie = sections[0]!;
  } else if (valeur < 0) {
    choisie = sections[1]!;
  } else {
    choisie = sections.length >= 3 ? sections[2]! : sections[0]!;
  }
  if (choisie.jetons.some((j) => j.t === 'general')) return formatGeneral(valeur);
  if (choisie.jetons.some((j) => j.t === 'texte')) return formatGeneral(valeur);
  if (estDate(choisie)) {
    return rendreDate(valeur, choisie, date1904) ?? '';
  }
  return rendreNombre(valeur, choisie, moins) ?? formatGeneral(valeur);
}
